/**
 * PrintWizard
 *
 * Full print workflow with stepper:
 * 1. Select assets + quantities
 * 2. Choose template
 * 3. Preview & print
 * 4. Completion summary
 */
import { CheckCircle } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useLabelTemplates } from '../../hooks/useLabelTemplates';
import { generatePreviewElements } from '../../services/printing';
import type { LabelTemplate, PrintAssetSelection } from '../../types/printing';
import { AssetSelection } from './AssetSelection';
import { LabelPreviewRenderer } from './LabelPreviewRenderer';
import { PrintPreview } from './PrintPreview';

const steps = ['Select Assets', 'Choose Template', 'Preview & Print', 'Done'];

interface PrintWizardProps {
  /** Pre-selected assets to print (e.g., from asset details page) */
  initialSelections?: PrintAssetSelection[];
  /** Called when wizard completes or is dismissed */
  onClose?: () => void;
}

export function PrintWizard({ initialSelections, onClose }: PrintWizardProps) {
  const [activeStep, setActiveStep] = useState(initialSelections?.length ? 1 : 0);
  const [selections, setSelections] = useState<PrintAssetSelection[]>(initialSelections || []);
  const [selectedTemplate, setSelectedTemplate] = useState<LabelTemplate | null>(null);
  const [companyName, setCompanyName] = useState('DigniCRM');

  const { templates, loading: templatesLoading } = useLabelTemplates();

  const handleTemplateSelect = (templateId: string) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (tmpl) setSelectedTemplate(tmpl);
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <AssetSelection
            selections={selections}
            onSelectionsChange={setSelections}
            onNext={() => setActiveStep(1)}
          />
        );

      case 1:
        return (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Choose Label Template
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Company / Brand Name"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                size="small"
                helperText="Appears on labels as {CompanyName}"
              />

              <FormControl fullWidth size="small">
                <InputLabel>Label Template</InputLabel>
                <Select
                  value={selectedTemplate?.id || ''}
                  label="Label Template"
                  onChange={e => handleTemplateSelect(e.target.value)}
                >
                  {templates.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      <Stack>
                        <Typography variant="body2">{t.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(t.width_dots / 203).toFixed(1)}&quot; x{' '}
                          {(t.height_dots / 203).toFixed(1)}&quot;
                          {t.is_default ? ' (Default)' : ''}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Template preview grid */}
              {!templatesLoading && (
                <Grid container spacing={2}>
                  {templates.map(tmpl => {
                    const isSelected = selectedTemplate?.id === tmpl.id;
                    // Show preview with first selected asset if available
                    const previewAsset = selections[0]?.asset;
                    const previewElements = previewAsset
                      ? generatePreviewElements(previewAsset, tmpl, { companyName })
                      : [];

                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tmpl.id}>
                        <Card
                          variant={isSelected ? 'elevation' : 'outlined'}
                          sx={{
                            cursor: 'pointer',
                            border: isSelected ? '2px solid' : undefined,
                            borderColor: isSelected ? 'primary.main' : undefined,
                            '&:hover': { boxShadow: 3 },
                          }}
                          onClick={() => {
                            setSelectedTemplate(tmpl);
                          }}
                        >
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {tmpl.name}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                              <LabelPreviewRenderer
                                elements={previewElements}
                                widthDots={tmpl.width_dots}
                                heightDots={tmpl.height_dots}
                                scale={0.3}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {(tmpl.width_dots / 203).toFixed(1)}&quot; x{' '}
                              {(tmpl.height_dots / 203).toFixed(1)}&quot;
                              {tmpl.description && ` — ${tmpl.description}`}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" onClick={() => setActiveStep(0)}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(2)}
                disabled={!selectedTemplate}
                size="large"
              >
                Next: Preview & Print
              </Button>
            </Stack>
          </Box>
        );

      case 2:
        return selectedTemplate ? (
          <PrintPreview
            selections={selections}
            template={selectedTemplate}
            companyName={companyName}
            onBack={() => setActiveStep(1)}
            onComplete={_jobId => {
              setActiveStep(3);
            }}
          />
        ) : null;

      case 3:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Print Job Submitted
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your labels have been sent to the printer. Check the print job log for status.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => {
                  setActiveStep(0);
                  setSelections([]);
                  setSelectedTemplate(null);
                }}
              >
                Print More
              </Button>
              {onClose && (
                <Button variant="contained" onClick={onClose}>
                  Done
                </Button>
              )}
            </Stack>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 3 }}>{renderStep()}</Paper>
    </Box>
  );
}
